import { useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Grid } from '@mui/material';
import { DEFAULT_CURRENCY } from '@procureai/schemas';
import { CurrencyProvider } from '@procureai/ui-mui';
import { forwardRef, useCallback } from 'react';
import { DeepPartial, FormProvider, useForm } from 'react-hook-form';

import { NegotiationCaseCreateWizardState } from '../NegotiationCaseCreateWizard/NegotiationCaseCreateWizard.schema';
import { useWizardState } from '~/components/WizardState/hooks/useWizardState/useWizardState';
import { useWizardFormHandle } from '~/components/WizardState/hooks/useWizardStateHandle/useWizardStateHandle';
import { WizardRef } from '~/components/WizardState/WizardState';

import { QuoteDetailsForm } from './components/QuoteDetailsForm/QuoteDetailsForm';
import { QuoteLineItemForm } from './components/QuoteLineItemForm/QuoteLineItemForm';
import { QUOTE_QUERY } from './Quote.query.gql';
import {
  refineQuoteDetailsStepFormDataDefault as defaultValues,
  refineQuoteDetailsStepFormDataSchema as schema,
  RefineQuoteDetailsStepFormData,
  toWizardStateMapper,
  SvbrgQuoteToFormStateMapper,
} from './RefineQuoteDetailsStep.schema';

export const RefineQuoteDetailsStep = forwardRef<WizardRef, any>((_props, ref) => {
  // TODO: Add default language from config here
  // const { defaultLanguage } = useConfig();

  const form = useForm<RefineQuoteDetailsStepFormData>({
    mode: 'onTouched',
    resolver: zodResolver(schema),
    defaultValues,
  });

  const { state, onError } = useWizardState<NegotiationCaseCreateWizardState>();

  useQuery(QUOTE_QUERY, {
    variables: { id: state.quote.id },
    onCompleted: (data) => {
      // Ensuring returned strings for e.g. decimal are converted to a number
      const convertedData = SvbrgQuoteToFormStateMapper.parse(data?.sevenbridges?.quote);
      form.reset({ ...convertedData });
    },
  }); // TODO: Include summary from Svbrg backend

  const toWizardState = useCallback(
    (formData: RefineQuoteDetailsStepFormData): DeepPartial<NegotiationCaseCreateWizardState> => {
      // parsing data from the form to ensure default values are included (mainly supplier.contact.language):
      const { success, data } = toWizardStateMapper.safeParse(formData);

      if (!success) {
        onError('Error parsing form data');
        throw new Error('Error parsing form data');
      }

      return data;
    },
    [onError],
  );

  useWizardFormHandle<RefineQuoteDetailsStepFormData, NegotiationCaseCreateWizardState>({
    form,
    toWizardState,
    ref,
  });

  return (
    <Box
      sx={{
        paddingTop: 1,
      }}
    >
      <FormProvider {...form}>
        <form noValidate>
          <>
            <CurrencyProvider value={form.watch().currency ?? DEFAULT_CURRENCY}>
              <Grid container spacing={4}>
                <QuoteDetailsForm />
              </Grid>
              <Box
                sx={{
                  mt: 6,
                }}
              >
                <QuoteLineItemForm />
              </Box>
            </CurrencyProvider>
          </>
        </form>
      </FormProvider>
    </Box>
  );
});
